// <img> backed by a private S3 blob: resolves its s3Key to a presigned GET URL
// (batched + cached) and shows a warm shimmer placeholder until it loads.

import { useEffect, useState } from "react";
import { peekAsset, resolveAsset } from "../lib/assetResolver";

export function useAssetUrl(s3Key: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(() =>
    s3Key ? peekAsset(s3Key) ?? null : null,
  );
  useEffect(() => {
    if (!s3Key) {
      setUrl(null);
      return;
    }
    const cached = peekAsset(s3Key);
    if (cached !== undefined) {
      setUrl(cached);
      return;
    }
    let cancelled = false;
    resolveAsset(s3Key).then((u) => {
      if (!cancelled) setUrl(u);
    });
    return () => {
      cancelled = true;
    };
  }, [s3Key]);
  return url;
}

interface AssetImageProps {
  s3Key: string | null | undefined;
  alt: string;
  className?: string;
  imgClassName?: string;
  onClick?: () => void;
}

export function AssetImage({ s3Key, alt, className, imgClassName, onClick }: AssetImageProps) {
  const url = useAssetUrl(s3Key);
  return (
    <div
      className={`relative overflow-hidden bg-cream ${onClick ? "cursor-pointer" : ""} ${className ?? ""}`}
      onClick={onClick}
    >
      {/* Shimmer only while the presigned URL is still resolving. */}
      {!url && <div className="absolute inset-0 animate-pulse bg-cream" />}
      {url && (
        <img
          src={url}
          alt={alt}
          loading="lazy"
          className={`h-full w-full object-cover ${imgClassName ?? ""}`}
        />
      )}
    </div>
  );
}
